/**
 * Implementazione del gateway su Supabase (PostgREST + Realtime).
 *
 * È l'unico file autorizzato a importare `@supabase/supabase-js`: la regola è
 * applicata da ESLint (`no-restricted-imports`). Se un componente dovesse mai
 * importare il client direttamente, la lint fallisce.
 */

import { createClient, type RealtimeChannel, type SupabaseClient } from '@supabase/supabase-js';
import {
  DataError,
  type DataGateway,
  type Filter,
  type RealtimeOptions,
  type RealtimeSubscription,
  type RpcArgs,
  type SelectQuery,
} from './gateway';

export interface SupabaseGatewayConfig {
  readonly url: string;
  readonly anonKey: string;
}

interface PostgrestErrorShape {
  message: string;
  code?: string;
  hint?: string;
  details?: string;
}

function toDataError(error: PostgrestErrorShape): DataError {
  return new DataError(error.message, error.code, error.hint, error.details);
}

/* eslint-disable @typescript-eslint/no-explicit-any -- PostgrestFilterBuilder non
   è esportato con un tipo utilizzabile senza conoscere lo schema generato; il
   costruttore di filtri resta comunque confinato a questa funzione. */
function applyFilters(builder: any, filters: readonly Filter[] | undefined): any {
  let result = builder;
  for (const filter of filters ?? []) {
    switch (filter.operator) {
      case 'in':
        result = result.in(filter.column, filter.value as unknown[]);
        break;
      case 'is':
        result = result.is(filter.column, filter.value as null | boolean);
        break;
      case 'contains':
        result = result.contains(filter.column, filter.value as unknown[]);
        break;
      case 'overlaps':
        result = result.overlaps(filter.column, filter.value as unknown[]);
        break;
      default:
        result = result[filter.operator](filter.column, filter.value);
    }
  }
  return result;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function createSupabaseGateway(config: SupabaseGatewayConfig): DataGateway & {
  client: SupabaseClient;
} {
  const client = createClient(config.url, config.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Lo storage predefinito (localStorage) è adeguato per una SPA: il token
      // ha vita breve ed è rinnovato da un refresh token ruotato da Supabase.
      // Nessun secret privilegiato raggiunge mai il browser.
      flowType: 'pkce',
    },
    global: {
      headers: { 'x-application-name': 'bacco' },
    },
  });

  const gateway: DataGateway & { client: SupabaseClient } = {
    kind: 'supabase',
    client,

    async select<T>(from: string, query: SelectQuery = {}): Promise<T[]> {
      let builder = client.from(from).select(query.columns ?? '*');
      builder = applyFilters(builder, query.filters);

      for (const order of query.order ?? []) {
        builder = builder.order(order.column, {
          ascending: order.ascending ?? true,
          nullsFirst: order.nullsFirst,
        });
      }
      if (query.limit !== undefined) {
        const offset = query.offset ?? 0;
        builder = builder.range(offset, offset + query.limit - 1);
      }

      const { data, error } = await builder;
      if (error) throw toDataError(error);
      return (data ?? []) as T[];
    },

    async selectOne<T>(from: string, query: SelectQuery = {}): Promise<T | null> {
      const rows = await gateway.select<T>(from, { ...query, limit: 1 });
      return rows[0] ?? null;
    },

    async rpc<T>(fn: string, args: RpcArgs = {}): Promise<T> {
      const { data, error } = await client.rpc(fn, args);
      if (error) throw toDataError(error);
      return data as T;
    },

    async insert<T>(into: string, values: Record<string, unknown> | Record<string, unknown>[]) {
      const { data, error } = await client.from(into).insert(values).select();
      if (error) throw toDataError(error);
      return (data ?? []) as T[];
    },

    async update<T>(table: string, values: Record<string, unknown>, filters: readonly Filter[]) {
      const builder = applyFilters(client.from(table).update(values), filters);
      const { data, error } = await builder.select();
      if (error) throw toDataError(error);
      return (data ?? []) as T[];
    },

    async remove<T>(table: string, filters: readonly Filter[]) {
      const builder = applyFilters(client.from(table).delete(), filters);
      const { data, error } = await builder.select();
      if (error) throw toDataError(error);
      return (data ?? []) as T[];
    },

    async invoke<T>(fn: string, body: Record<string, unknown>): Promise<T> {
      const { data, error } = await client.functions.invoke(fn, { body });
      if (error) {
        // Le Edge Function di Bacco rispondono `{ error: "…" }` in italiano:
        // quel messaggio spiega più del codice HTTP.
        const context = (error as { context?: Response }).context;
        let message = error.message;
        let status: number | undefined;
        if (context instanceof Response) {
          status = context.status;
          const payload = (await context.json().catch(() => null)) as {
            error?: string;
            message?: string;
            msg?: string;
          } | null;
          // `error` è il formato delle funzioni di Bacco; `message`/`msg` quello
          // della piattaforma Supabase quando rifiuta la richiesta prima.
          const detail = payload?.error ?? payload?.message ?? payload?.msg;
          if (detail) message = `${detail} (${status})`;
        }
        throw new DataError(message, status ? `HTTP${status}` : undefined, undefined, undefined);
      }
      return data as T;
    },

    subscribe(options: RealtimeOptions): RealtimeSubscription {
      const channelName = `bacco:${options.table}:${options.filter ?? 'all'}`;
      let channel: RealtimeChannel | null = client
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: options.table,
            ...(options.filter ? { filter: options.filter } : {}),
          },
          () => options.onChange(),
        )
        .subscribe((status) => {
          // Alla riconnessione i messaggi persi non vengono recapitati: si forza
          // un nuovo caricamento, che è la sola strategia corretta. Il realtime
          // notifica *che* qualcosa è cambiato, non sostituisce la lettura.
          if (status === 'SUBSCRIBED') options.onChange();
        });

      return {
        unsubscribe() {
          if (channel) {
            void client.removeChannel(channel);
            channel = null;
          }
        },
      };
    },
  };

  return gateway;
}
